import Ship from 'asteroids-shared/entities/Ship'
import {
  WORLD_WIDTH, WORLD_HEIGHT
} from 'asteroids-shared/constants'
import { shortestAngleDeltaDeg, wrappedDelta } from 'asteroids-shared/helpers'

const DEFAULT_BOUNDS = { width: WORLD_WIDTH, height: WORLD_HEIGHT, toroidalMap: true }

// Exponential decay time for post-reconcile render offset (ms).
const RENDER_SMOOTH_TIME_MS = 100

// Above this distance from server pose, clear render offset (teleport / respawn).
const RECONCILE_TELEPORT_DIST = 200

// Local ship: predicted physics, input history, server reconciliation, render smoothing. ClientGame wires input + snapshots.
export default class ShipSim {
  constructor({ shipId, color, name, worldBounds = DEFAULT_BOUNDS }) {
    this.worldBounds = worldBounds
    this.ship = new Ship({
      id: shipId,
      position: { x: worldBounds.width / 2, y: worldBounds.height / 2 },
      color, name
    })
    this.history = []
    this.seq = 0
    this.initialized = false
    this.renderError = { x: 0, y: 0, rotation: 0 }
  }

  // Run one local simulation tick. Returns { seq, keys, wallBounce } to send to server (wallBounce for client audio only).
  tick(keys) {
    this.seq += 1
    const snapped = { ...keys }
    this.history.push({ seq: this.seq, keys: snapped })
    let wallBounce = false
    if (this.ship.alive) {
      this.ship.applyInput(snapped)
      const step = this.ship.update(1, this.worldBounds)
      wallBounce = !!step?.wallBounce
    }
    if (this.history.length > 600) this.history.shift()
    return { seq: this.seq, keys: snapped, wallBounce }
  }

  // Apply an authoritative server ship state and replay unacked inputs.
  reconcile(serverState) {
    const prevX = this.ship.position.x
    const prevY = this.ship.position.y
    const prevRotation = this.ship.rotation
    const wasInitialized = this.initialized

    this.ship.setState({
      x: serverState.x, y: serverState.y,
      vx: serverState.vx, vy: serverState.vy, rotation: serverState.rotation, av: serverState.av,
      shieldHealth: serverState.shieldHealth,
      shieldMax: serverState.shieldMax
    })
    this.ship.alive = serverState.alive
    if (Number.isFinite(serverState.invulnerableUntil)) {
      this.ship.invulnerableUntil = serverState.invulnerableUntil
    }
    if (Number.isFinite(serverState.lastShotAt)) {
      this.ship.lastShotAt = serverState.lastShotAt
    }
    if (Number.isFinite(serverState.fireTokens)) {
      this.ship.fireTokens = serverState.fireTokens
    }
    if (Number.isFinite(serverState.lastTokenUpdateMs)) {
      this.ship.lastTokenUpdateMs = serverState.lastTokenUpdateMs
    }
    if (Number.isFinite(serverState.respawnAt)) {
      this.ship.respawnAt = serverState.respawnAt
    }
    this.initialized = true

    const ackedSeq = serverState.lastAckedSeq ?? 0
    this.history = this.history.filter(h => h.seq > ackedSeq)

    if (this.ship.alive) {
      for (const h of this.history) {
        this.ship.applyInput(h.keys)
        void this.ship.update(1, this.worldBounds)
      }
    }

    if (!wasInitialized) {
      this.renderError.x = 0
      this.renderError.y = 0
      this.renderError.rotation = 0
      return
    }

    const dx = this.worldBounds.toroidalMap === false
      ? prevX - this.ship.position.x
      : wrappedDelta(prevX, this.ship.position.x, this.worldBounds.width)
    const dy = this.worldBounds.toroidalMap === false
      ? prevY - this.ship.position.y
      : wrappedDelta(prevY, this.ship.position.y, this.worldBounds.height)
    const deltaRotation = shortestAngleDeltaDeg(prevRotation - this.ship.rotation)
    const dist = Math.hypot(dx, dy)

    if (dist > RECONCILE_TELEPORT_DIST) {
      this.renderError.x = 0
      this.renderError.y = 0
      this.renderError.rotation = 0
      return
    }

    this.renderError.x += dx
    this.renderError.y += dy
    this.renderError.rotation += deltaRotation
  }

  decayRenderError(elapsedMs) {
    if (elapsedMs <= 0) return
    const k = Math.exp(-elapsedMs / RENDER_SMOOTH_TIME_MS)
    const e = this.renderError
    e.x *= k
    e.y *= k
    e.rotation *= k
    if (e.x * e.x + e.y * e.y < .01) {
      e.x = 0
      e.y = 0
    }
    if (Math.abs(e.rotation) < .1) e.rotation = 0
  }

  renderedPose() {
    const e = this.renderError
    return {
      x: this.ship.position.x + e.x,
      y: this.ship.position.y + e.y,
      rotation: this.ship.rotation + e.rotation
    }
  }

  onDied() {
    this.ship.alive = false
    this.ship.invulnerableUntil = 0
    this.history = []
    this.renderError.x = 0
    this.renderError.y = 0
    this.renderError.rotation = 0
  }
}
